<script>
import useSharedData from "@/use/SharedData.js";
import useValidation from "@/use/Validation.js";
import useTools from "@/use/Tools.js";
import {computed, onMounted, ref} from "vue";
import {useTrainingRequestStore} from "@/stores/Training/TrainingRequest.js";
import useSharedMethod from "@/use/SharedMethod.js";
import StrCatalogAutocomplete from "@/views/Training/Catalog/Components/StrCatalogAutocomplete.vue";
import useSharedSelectData from "@/use/SharedSelectData";
import axios from "axios";
import WCodeGeneration from "@/components/WCodeGeneration.vue";

export default {
    name: "TrainingRequestForm",
    components: {WCodeGeneration, StrCatalogAutocomplete},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },

    setup(props) {
        const {mxRules} = useValidation();
        const {loading, dialog, overlay, inputColor, backgroundColor} = useSharedData();
        const {$route, $refs} = useTools();
        const {trainingPeriods, quarterlies, halfYearlies, months} = useSharedSelectData();
        const {getBack} = useSharedMethod();

        const trainingRequestStore = useTrainingRequestStore();

        const readonly = computed(() => props.viewAction || props.deleteAction);

        const trainingRequest = ref({
            other: false,
            workerIds: []
        });


        function findById(id) {
            loading.value = true;
            axios.get(`/trainingRequests/${id}`).then((response) => {
                trainingRequest.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/trainingRequests', trainingRequest.value).then((response) => {
                    trainingRequestStore.addTrainingRequest(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/trainingRequests', trainingRequest.value).then((response) => {
                    trainingRequestStore.updateTrainingRequest(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/trainingRequests/${trainingRequest.value.id}`).then(() => {
                trainingRequestStore.deleteTrainingRequest(trainingRequest.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            trainingPeriods,
            quarterlies,
            halfYearlies,
            months,
            trainingRequest,
            inputColor,
            backgroundColor,
            mxRules,
            readonly,
            loading,
            dialog,
            overlay,
            save,
            update,
            remove,
            getBack,
        };
    },
};
</script>

<template>
    <v-dialog
        v-model="dialog"
        persistent
        max-width="800"
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            ></w-dialog-toolbar>
            <w-overlay
                :loading="loading"
                :overlay="overlay"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            ></w-overlay>

            <v-card-text>
                <v-form ref="form" lazy-validation>
                    <v-container fluid>
                        <w-fieldset :title="$t('common.details')">
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('common.code')" required/>
                                    <w-code-generation
                                        v-model="trainingRequest.code"
                                        :entity="'TRAINING_REQUEST'"
                                        :load="addAction"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <w-label :label="$t('training.department')" required/>
                                    <organizational-chart-field
                                        v-model="trainingRequest.organizationalChartId"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <w-label :label="$t('recruitment_request.applicant')" required/>
                                    <str-worker-autocomplete
                                        v-model="trainingRequest.workerId"
                                        action="SAVE_TRAINING_REQUEST"
                                        :readonly="readonly"
                                        :organizationalchart-id="trainingRequest.organizationalChartId"
                                        load-by-organizationalchart-id
                                        :disabled="!trainingRequest.organizationalChartId"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('training.theme')" required/>
                                    <str-catalog-autocomplete
                                        v-model="trainingRequest.catalogId"
                                        :readonly="readonly"
                                        :disabled="trainingRequest.other"
                                        :rules="trainingRequest.other ? [] : [mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-checkbox
                                        v-model="trainingRequest.other"
                                        hide-details
                                        class="mt-n1"
                                        @change="$refs.form.resetValidation()"
                                        :label="$t('training.other_catalog')"
                                        :readonly="readonly"
                                    />
                                </v-col>
                                <v-col cols="12" v-if="trainingRequest.other">
                                    <w-text-field
                                        v-model="trainingRequest.otherDescription"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset :title="`${$t('training.desired_workers')} (${trainingRequest.workerIds.length})`">
                            <v-row>
                                <v-col cols="12">
                                    <str-worker-autocomplete
                                        v-model="trainingRequest.workerIds"
                                        multiple
                                        :readonly="readonly"
                                        :rules="[mxRules.required, mxRules.listRequired]"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset :title="$t('training.desired_period')">
                            <v-row dense>
                                <v-col cols="4">
                                    <w-label :label="$t('training.period')" required/>
                                    <w-enum-select
                                        v-model="trainingRequest.period"
                                        :items="trainingPeriods"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="4" v-if="trainingRequest.period === 'MONTHLY'">
                                    <w-label :label="$t('common.month')" required/>
                                    <w-enum-select
                                        v-model="trainingRequest.periodMonth"
                                        :items="months"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="4" v-if="trainingRequest.period === 'QUARTERLY'">
                                    <w-label :label="$t('common.QUARTERLY')" required/>
                                    <w-enum-select
                                        v-model="trainingRequest.periodQuarterlyOrHalfYearly"
                                        :items="quarterlies"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="4" v-if="trainingRequest.period === 'HALF_YEARLY'">
                                    <w-label :label="$t('common.HALF_YEARLY')" required/>
                                    <w-enum-select
                                        v-model="trainingRequest.periodQuarterlyOrHalfYearly"
                                        :items="halfYearlies"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="4" v-if="trainingRequest.period === 'YEARLY'">
                                    <w-label :label="$t('common.YEARLY')" required/>
                                    <w-text-area
                                        v-model="trainingRequest.periodYearly"
                                        type="number"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="4" v-if="trainingRequest.period === 'SPECIFIC'">
                                    <w-label :label="$t('common.from')" required/>
                                    <w-date-picker
                                        v-model="trainingRequest.periodStart"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="4" v-if="trainingRequest.period === 'SPECIFIC'">
                                    <w-label :label="$t('common.to')" required/>
                                    <w-date-picker
                                        v-model="trainingRequest.periodEnd"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset :title="$t('common.comment')">
                            <v-row dense>
                                <v-col cols="12">
                                    <w-simple-text-area
                                        v-model="trainingRequest.description"
                                        :readonly="readonly"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>